import { useEffect, useState } from 'react';
import { Dropdown } from '../ui';
import { fetchNoteTemplates, type NoteTemplate } from '../../lib/noteTemplates';
import AttachmentDropzone from './AttachmentDropzone';
import type { AttachmentPicker } from './attachmentPicker';
import ModelPicker from './ModelPicker';

interface SpecQueueNoteFieldsProps {
  note: string;
  onNoteChange: (note: string) => void;
  picker: AttachmentPicker;
  disabled: boolean;
  /** Pinned model for the new job. Omit both to hide the picker. */
  model?: string | null;
  onModelChange?: (model: string | null) => void;
  style?: React.CSSProperties;
}

/**
 * "Note for Owlbert" + attachments + model — the human context that travels
 * with a queue item. Shared by both spec queue panels so they read identically.
 * A canned note (Agents → Note templates) picked from the dropdown replaces the
 * note's text and stays editable; the dropdown hides when there are none.
 */
export default function SpecQueueNoteFields({
  note,
  onNoteChange,
  picker,
  disabled,
  model,
  onModelChange,
  style,
}: SpecQueueNoteFieldsProps) {
  const [templates, setTemplates] = useState<NoteTemplate[]>([]);
  const [templateId, setTemplateId] = useState('');

  // No templates is better than no drawer: a failed load just hides the dropdown.
  useEffect(() => {
    let live = true;
    fetchNoteTemplates()
      .then((t) => live && setTemplates(t))
      .catch((err) => console.error('Error loading note templates:', err));
    return () => {
      live = false;
    };
  }, []);

  const pickTemplate = (id: string) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setTemplateId(id);
    onNoteChange(t.body);
  };

  return (
    <div style={style}>
      {onModelChange ? (
        <ModelPicker
          variant="field"
          value={model ?? null}
          onChange={onModelChange}
          disabled={disabled}
          hint="Which LLM runs this job. Default runs on the agent's own model, currently Opus 5.5. Changeable from the queue card until an agent picks it up."
          style={{ marginBottom: '14px' }}
        />
      ) : null}
      {templates.length ? (
        <div style={{ marginBottom: '14px', ...(disabled ? { pointerEvents: 'none', opacity: 0.6 } : null) }}>
          <Dropdown
            label="Canned note"
            placeholder="Load a note template…"
            options={templates.map((t) => ({ value: t.id, label: t.name }))}
            value={templateId}
            onChange={(e) => pickTemplate(e.target.value)}
          />
        </div>
      ) : null}
      <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <span className="t-eyebrow">Note for Owlbert</span>
        <textarea
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder="Optional. Travels with this queue item."
          rows={4}
          disabled={disabled}
          style={{
            width: '100%',
            resize: 'vertical',
            minHeight: '88px',
            padding: '10px 12px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border-hairline)',
            background: 'var(--surface-input)',
            color: 'var(--text-body)',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--text-sm)',
            lineHeight: 1.45,
            outline: 'none',
          }}
        />
      </label>
      <div style={{ marginTop: '14px' }}>
        <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
          Attachments{picker.files.length > 0 ? ` (${picker.files.length})` : ''}
        </div>
        <AttachmentDropzone
          compact
          files={picker.files}
          error={picker.error}
          onAdd={picker.add}
          onRemove={picker.remove}
          disabled={disabled}
        />
        <p style={{ margin: '6px 0 0', fontSize: 'var(--text-xs)', color: 'var(--text-faint)', lineHeight: 1.5 }}>
          Specs, .md notes, screenshots, data — anything Owlbert should read first. Links land in the note.
        </p>
      </div>
    </div>
  );
}
